/* eslint-disable @next/next/no-img-element */
import React, { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';
import {
  openAccountProfilePage,
  getAccessToken,
} from '@/common/utils/accountUtil';
import Image from 'next/image';
import { Popover, Switch } from 'antd';
import { getGid } from '@/utils';
import { getEnv, navigate } from '@/common/agents';
import dayjs from 'dayjs';
import Modal from '$/components/base/Modal';
import Button from '$/components/base/Button';
import useThemeStore, { setTheme } from '$/store/useThemeStore';
import { useLanguageStore, type LanguageCodeType } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { message } from '$/components/Message';
import Select from '$/components/base/Select';
import IconFont from '$/components/IconFont';
import localStorageUtil from '$/common/utils/localStorageUtil';
import useSubScribeStore from '$/store/useSubscribeStore';
import { isMobile } from '$/utils';
import {
  getAssetStorageInfo,
  getAssetStorageProgressBarStyle,
} from '$/utils/assetStorage';
import { openVipModal } from '$/store/useVipModalStore';
import { copyText } from '$/utils/webUtil';
import { trackRoboneoVipClick, trackRoboneoVipShow } from '$/utils/vipPromptTrack';
import { useCommonStore } from '@/store/useCommonStore';
import type { FeedbackEnv } from '@meitu/feedback';
import classNames from 'classnames';
import { getTeamList } from '@/domains/team/teamService';
import TeamPackageBadge from '@/components/TeamPackageBadge';
import useTeamStore, { setTeamInfo } from '@/store/useTeamStore';
import { TeamListItem } from '@/domains/team/types';
import styles from './index.module.scss';
import FeedbackModal from '../FeedbackModal';
import ReportModal from '../ReportModal';
import VipImg from './vip.svg';
import UserTeamAccountList from './UserTeamAccountList';

const CreateTeamModal = dynamic(
  () => import('./CreateTeamModal').then((mod) => mod.default),
  { ssr: false },
);

const TeamManagementModal = dynamic(
  () => import('@/components/TeamManagement/Modal').then((mod) => mod.default),
  { ssr: false },
);

interface UserCardProps {
  style?: React.CSSProperties;
  rightMargin?: number;
  options?: {
    hiddenSystemSetting: boolean;
  };
  variant?: 'default' | 'teamFlowHeader';
}

export default UserCard;

/** 用户头像卡片及菜单入口，TeamFlow 头部变体会隐藏会员角标以匹配画布工具栏设计。 */
function UserCard({
  style,
  rightMargin,
  options = {
    hiddenSystemSetting: false,
  },
  variant = 'default',
}: UserCardProps) {
  const teamInfo = useTeamStore((state) => state.teamInfo);
  const teamRoleText = useTeamStore((state) => state.teamRoleText());
  const { hiddenSystemSetting = false } = options || {};
  const languageStore = useLanguageStore((state) => state.languageStore);
  const loadSubscribeInfo = useOfficialStore(
    (state) => state.loadSubscribeInfo,
  );
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const currentCountryCode = useOfficialStore(
    (state) => state.currentCountryCode,
  );
  const toLogout = useCommonStore((state) => state.toLogout);
  const isOnline = useCommonStore((state) => state.isOnline);
  const toLogin = useCommonStore((state) => state.toLogin);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const isOversea = useOfficialStore((state) => state.isOversea());
  const userInfo = useOfficialStore((state) => state.userInfo);
  const hasPay = useSubScribeStore((state) => state.hasPay);
  const theme = useThemeStore((state) => state.theme);
  const assetStorageInfo = getAssetStorageInfo(userInfo.subscribe);
  const assetStorageProgressBarStyle = getAssetStorageProgressBarStyle(assetStorageInfo);
  const [local, setLocal] = useState({
    loggedOutTipVisible: false,
    showFeedback: false,
    showReport: false,
    showVip: true,
    showSettings: false,
    showAccessKey: false,
    languageCode: '' as LanguageCodeType | '',
    unreadCount: 0,
  });
  const showMembershipBadge = variant !== 'teamFlowHeader';

  const updateLocal = (newValues: Partial<typeof local>) => {
    setLocal((prev) => ({ ...prev, ...newValues }));
  };

  const [teamList, setTeamList] = useState<TeamListItem[]>([]);

  useEffect(() => {
    setLocal((prev) => ({
      ...prev,
      languageCode:
        (localStorageUtil.getItem('lang') as LanguageCodeType) || '',
    }));
    initFeedbackUnreadMessages();
  }, []);

  /**
   * 拉取团队列表，并据接口返回的当前身份同步 team store。
   */
  const refreshTeamList = useCallback(() => {
    if (!isUserLoggedIn) {
      setTeamList([]);
      return;
    }

    getTeamList()
      .then((res) => {
        const nextTeamList = res.list ?? [];
        setTeamList(nextTeamList);
        // 团队列表接口会返回当前身份；这里同步给 SDK 请求上下文，避免订阅弹窗只拿到个人态。
        const listedCurrentTeam = nextTeamList.find((item) => item.oid === res.current_oid);
        let currentTeam: TeamListItem | undefined;
        if (res.current_identity === 'team') {
          currentTeam = res.current_team?.client_ogroupid
            ? res.current_team
            : listedCurrentTeam || res.current_team;
        }

        if (currentTeam?.client_ogroupid) {
          const storedTeamInfo = useTeamStore.getState().teamInfo;
          if (
            storedTeamInfo?.oid !== currentTeam.oid
            || storedTeamInfo?.client_ogroupid !== currentTeam.client_ogroupid
          ) {
            setTeamInfo(currentTeam);
          }
        }
      })
      .catch((error) => {
        console.error('getTeamList error:', error);
      });
  }, [isUserLoggedIn]);

  useEffect(() => {
    refreshTeamList();
  }, [refreshTeamList]);

  useEffect(() => {
    if (hasPay) {
      loadSubscribeInfo();
    }
  }, [hasPay]);

  const handleCopy = async () => {
    try {
      await copyText(userInfo.uid);
      message.success('RoboNeo_Web_copy_success');
    } catch (err) {
      message.error('RoboNeo_Web_copy_fail');
      console.error('复制失败:', err);
    }
  };
  function capitalize(str: string) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  async function initFeedbackUnreadMessages() {
    // 客服反馈sdk不支持移动端
    if (isMobile()) return;
    try {
      const { initIMInstance, UnreadKey } = await import('@meitu/feedback');
      const instance = await initIMInstance({
        debug: false,
        env: capitalize(getFebackEnv()) as FeedbackEnv,
        apiConfigs: {
          accessToken: getAccessToken(),
          clientId: getEnv().CLIENT_ID,
          gid: getGid(),
        },
      });
      instance?.subscribeUnreadCount((data) => {
        if (data) {
          const unreadCount = data[UnreadKey.FeedbackUnreadCount] || 0;
          setLocal((prev) => ({ ...prev, unreadCount }));
        }
      });
    } catch (err) {
      console.warn('[feedback] init im failed', err);
    }
  }

  const loggedOutTip = () => {
    return (
      <div className={styles.loggedOutTip}>
        <div className={styles.loggedOutTitle}>
          {languageStore.smartcatTranslationText(
            (v) => v.RoboNeo_Web_toast_logout_text,
          )}
        </div>
        <div className={styles.loggedOutDetail}>
          {languageStore.smartcatTranslationText(
            (v) => v.RoboNeo_Web_toast_logout_main_text,
          )}
        </div>
      </div>
    );
  };

  // AIGC 水印
  const [openWatermarkModal, setOpenWatermarkModal] = useState(false);
  const [hasWatermakr, setHasWatermark] = useState(true);
  useEffect(() => {
    if (local.showSettings) {
      setHasWatermark(!(localStorageUtil.getItem('RoboneoDownloadHasWatermark') === '0'));
    }
  }, [local.showSettings]);

  // 切换开关
  const changeWatermarkHandler = (value: boolean) => {
    const cacheValue = localStorageUtil.getItem('RoboneoDownloadHasWatermark');
    // 首次关闭时弹窗确认
    if (cacheValue === null && !value) {
      setOpenWatermarkModal(true);
      return;
    }
    setHasWatermark(value);
    localStorageUtil.setItem('RoboneoDownloadHasWatermark', value ? '1' : '0');
  };

  // 弹窗确认关闭
  const continueCloseWatermarkHandler = () => {
    setHasWatermark(false);
    localStorageUtil.setItem('RoboneoDownloadHasWatermark', '0');
    setOpenWatermarkModal(false);
  };

  const handleCliLinkClick = () => {
    const url = currentCountryCode === 'CN' ? 'https://www.roboneo.com/cli/zh' : 'https://www.roboneo.com/cli/en';
    window.open(url);
  };

  const handleCopyAccessKey = async () => {
    try {
      const text = `npm install -g roboneo-cli && export ROBONEO_ACCESS_KEY=${userInfo.token}`;
      await copyText(text);
      window.mtstat?.track('roboneo_cli_secret_key_copy_click_', {
        key: `${userInfo.token.slice(0, 5)}********${userInfo.token.slice(-5)}`,
      });
      // 与分享对话复制一致：toast 与关弹窗延后到下一宏任务，避免 iOS Safari 因 DOM 变化中断剪贴板写入
      setTimeout(() => {
        message.success('access_keys_copied');
        updateLocal({ showAccessKey: false });
      }, 0);
    } catch (err) {
      message.error('RoboNeo_Web_copy_fail');
      console.error('复制失败:', err);
    }
  };

  const accessKey = () => {
    return (
      <div className={styles.accessKeyBox}>
        <div className={styles.accessKeyDescription}>
          {languageStore.smartcatTranslationText(
            (v) => v.my_access_keys_caution,
          )}
          <span className={styles.link} onClick={handleCliLinkClick}>
            &nbsp;
            {'>> RoboNeo CLI'}
          </span>
        </div>
        <div className={styles.accessKeyContent}>
          <div className={styles.accessKeyToken}>
            {`${userInfo.token.slice(0, 5)}********${userInfo.token.slice(-5)}`}
          </div>
          <Button type="primary" onClick={handleCopyAccessKey}>
            <IconFont name="duplicateV2Bold" fontSize={16} style={{ marginRight: 3 }} />
            {languageStore.smartcatTranslationText(
              (v) => v.access_keys_copy,
            )}
          </Button>
        </div>
      </div>
    );
  };

  const settings = () => {
    return (
      <div className={styles.settingsBox}>

        {/* 主题 */}
        <div className={styles.settings}>
          <div>
            {languageStore.smartcatTranslationText((v) => v.roboneo_web_theme)}
          </div>
          <div className={styles.right_box}>
            <Select
              value={theme}
              options={[
                {
                  value: 'light',
                  label: languageStore.smartcatTranslationText(
                    (v) => v.roboneo_web_light_theme,
                  ),
                },
                {
                  value: 'dark',
                  label: languageStore.smartcatTranslationText(
                    (v) => v.roboneo_web_dark_theme,
                  ),
                },
              ]}
              onChange={(value) => {
                setTheme(value);
                window.mtstat?.track('theme_setting', {
                  theme: value === 'light' ? 'white_neo' : 'black_neo',
                });
                Cookies.set('theme', value);
              }}
            />
          </div>
        </div>

        {/* 水印 */}
        {!isOversea && (
        <div className={styles.settings}>
          <div>
            {/* AIGC 水印 */}
            {languageStore.smartcatTranslationText((v) => v.roboneo_web_aigc_watermark)}
            <div className={styles.sub_title}>
              {/* 关闭后，保存内容将不展示 AIGC 水印 */}
              {languageStore.smartcatTranslationText((v) => v.roboneo_web_saved_no_aigc_watermark)}
            </div>
          </div>
          <div className={styles.right_box}>
            <Switch checked={hasWatermakr} onChange={changeWatermarkHandler} />
          </div>
        </div>
        )}

      </div>
    );
  };

  const getFebackEnv = () => {
    const env = getEnv().ENV;
    if (env === 'development' || env === 'dev') {
      return 'pre';
    }
    return env as 'pre' | 'beta' | 'release';
  };

  const handleReportClick = () => {
    updateLocal({ showReport: true });
    window.mtstat?.track('report_submit_click', {});
  };

  const handleFeedbackClick = () => {
    updateLocal({ showFeedback: true });
    window.mtstat?.track('feedback_click', {});
  };

  /** 用户卡片容量 CTA：最高等级会员走反馈入口，团队身份下显式打开团队版订阅。 */
  const handleAssetStorageAction = () => {
    const latestInfo = getAssetStorageInfo(useOfficialStore.getState().userInfo.subscribe);
    if (latestInfo.isHighestLevel) {
      handleFeedbackClick();
      return;
    }
    trackRoboneoVipClick('asset_account_entry');
    const latestTeamInfo = useTeamStore.getState().teamInfo;
    openVipModal(
      'topright_page',
      undefined,
      { from_source: 'asset_account_entry' },
      { membershipMode: latestTeamInfo ? 'team' : 'personal' },
    );
  };

  const handleSettingsClick = () => {
    updateLocal({ showSettings: true });
  };

  const [openTeamManagementModal, setOpenTeamManagementModal] = useState(false);

  /**
   * 从用户菜单打开团队管理全屏弹窗，不改变当前页面路由。
   */
  const handleTeamSettingsClick = useCallback(() => {
    const subscribe = useOfficialStore.getState().userInfo?.subscribe;
    const { radishAmount } = useOfficialStore.getState();
    const oid = useTeamStore.getState().teamInfo?.oid ?? '';
    window.mtstat?.track('roboneo_team_portrait_admin_click', {
      vip_level: subscribe?.level,
      meiye_num: radishAmount,
      oid,
    });
    setOpenTeamManagementModal(true);
  }, []);

  /**
   * 关闭团队管理全屏弹窗并保留底层页面状态。
   */
  const handleTeamManagementClose = useCallback(() => {
    setOpenTeamManagementModal(false);
  }, []);

  const handleAccessKeyClick = () => {
    window.mtstat?.track('roboneo_cli_secret_key_enter_click', {});
    updateLocal({ showAccessKey: true });
  };

  const [openCreateTeamModal, setOpenCreateTeamModal] = useState(false);

  return (
    <>
      <div className={styles.UserCard} style={style}>
        {isUserLoggedIn ? (
          <div className={styles.userLoggedInCard}>
            <Popover
              onOpenChange={(visible) => {
                if (visible) {
                  const subscribe = useOfficialStore.getState().userInfo?.subscribe;
                  const { radishAmount } = useOfficialStore.getState();
                  console.log('userInfo', userInfo);
                  window.mtstat?.track('roboneo_portrait_list_show', {
                    vip_level: subscribe?.level,
                    meiye_num: radishAmount,
                  });
                  trackRoboneoVipShow('asset_account_entry');
                  loadSubscribeInfo();
                }
              }}
              placement="bottomRight"
              trigger={['hover']}
              overlayStyle={{
                zIndex: 'calc(var(--zindex-fixed) + 1) !important',
              }}
              overlayClassName="resetPopoverAndArrow"
              content={(
                <div className={styles.popoverContent} style={{ width: '200px' }}>
                  <div className={styles.userInfoBox}>
                    <div
                      className={styles.userNameBox}
                      onClick={() => {
                        window.mtstat?.track('setting_page_account_click', {});
                        openAccountProfilePage(isOversea, {
                          blank: !isMobile(),
                        });
                      }}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={userInfo.avatar} alt="avatar" className={styles.avatar} crossOrigin="anonymous" />
                      <div className={styles.userName}>{userInfo.screenName}</div>
                      <IconFont
                        name="chevronRightFill"
                        fontSize={10}
                        color="var(--content-quaternary)"
                        className={styles.chevronRightFill}
                      />
                    </div>

                    {/* 团队标签 */}
                    {teamInfo && (
                    <div className={styles.userTeamTags}>
                      <span className={styles.teamRoleBadge}>{teamRoleText}</span>
                      <TeamPackageBadge teamInfo={teamInfo} className={styles.teamPackageBadge} />
                    </div>
                    )}

                    <div className={styles.uidBox}>
                      <IconFont
                        name="copyBold"
                        fontSize={10}
                        color="var(--content-tertiary)"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          handleCopy();
                        }}
                      />
                      <div>{`UID：${userInfo.uid}`}</div>
                    </div>
                    <div className={styles.btnLine} />
                    {/* 团队/个人切换 */}
                    {
                    (!!teamInfo || teamList.length > 0) && (
                      <Popover
                        placement="leftTop"
                        trigger={['hover']}
                        onOpenChange={(open) => {
                          if (open) {
                            refreshTeamList();
                          }
                        }}
                        overlayStyle={{
                          zIndex: 'calc(var(--zindex-fixed) + 2) !important',
                        }}
                        overlayClassName="resetPopoverAndArrow"
                        content={(
                          <UserTeamAccountList
                            teamList={teamList}
                            onCreateTeam={() => setOpenCreateTeamModal(true)}
                          />
                      )}
                      >
                        <Button
                          type="plain"
                          block
                          className={styles.teamPersonSwitch}
                          leftIcon={
                            <IconFont name="arrowLeftAndRightV2Bold" fontSize={14} color="var(--content-buttonSecondary)" />
                    }
                        >
                          {teamInfo
                            ? languageStore.smartcatTranslationText((v) => v.account)
                            : languageStore.smartcatTranslationText((v) => v.team_account)}
                        </Button>
                      </Popover>
                    )
                  }
                    {userInfo.subscribe?.isValid ? (
                      <div className={styles.invalidTimeBox}>
                        <div className={styles.invalidTimeTip}>
                          {languageStore.smartcatTranslationText(
                            (v) => v.membership_valid_until_august_28_2025,
                          )}
                          {dayjs(userInfo.subscribe?.invalidTime).format(
                            'YYYY/MM/DD',
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  {/* 免费团队版入口 */}
                  {teamList.length === 0 && (
                  <div className={styles.teamFreeEntry}>
                    <span className={styles.teamFreeEntryTitle}>
                      { languageStore.smartcatTranslationText((v) => v.free_team_edition) }
                    </span>
                    <Button
                      className={styles.teamFreeEntryButton}
                      onClick={() => {
                        const subscribe = useOfficialStore.getState().userInfo?.subscribe;
                        const { radishAmount } = useOfficialStore.getState();
                        window.mtstat?.track('roboneo_team_team_switch_click', {
                          vip_level: subscribe?.level,
                          meiye_num: radishAmount,
                          click_type: 'open_team',
                        });
                        setOpenCreateTeamModal(true);
                      }}
                    >
                      { languageStore.smartcatTranslationText((v) => v.activate_team_membership) }
                    </Button>
                  </div>
                  )}

                  {/* 资产库容量 */}
                  {assetStorageInfo.isReady ? (
                    <div className={classNames(styles.assetStorageCard, {
                      [styles.assetStorageCardExceeded]: assetStorageInfo.isExceeded,
                    })}
                    >
                      <div className={styles.assetStorageContent}>
                        <div className={styles.assetStorageRow}>
                          <span>{languageStore.smartcatTranslationText((v) => v.capacity_1)}</span>
                          <span>{assetStorageInfo.usageLabel}</span>
                        </div>
                        <div className={styles.assetStorageTrack}>
                          <span
                            className={styles.assetStorageBar}
                            style={assetStorageProgressBarStyle}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className={classNames(styles.assetStorageButton, {
                          [styles.assetStorageButtonPrimary]: assetStorageInfo.isHighestLevel,
                        })}
                        onClick={handleAssetStorageAction}
                      >
                        {assetStorageInfo.isHighestLevel
                          ? languageStore.smartcatTranslationText((v) => v.contact_us)
                          : languageStore.smartcatTranslationText((v) => v.upgrade_capacity_1)}
                      </button>
                    </div>
                  ) : null}

                  <div className={styles.line} />

                  {/* 操作列表 */}
                  <div className={styles.operateList}>
                    {/* 团队设置 */}
                    {teamInfo && (
                      <div className={styles.operateItem} onClick={handleTeamSettingsClick}>
                        <IconFont name="userGroupV3Bold" fontSize={16} />
                        {languageStore.smartcatTranslationText((v) => v.team_setup)}
                      </div>

                    )}

                    {/* 系统设置 */}
                    {!hiddenSystemSetting
                  && (
                  <div
                    className={styles.operateItem}
                    onClick={handleSettingsClick}
                  >
                    <IconFont name="gearV2Bold" fontSize={16} />
                    {languageStore.smartcatTranslationText(
                      (v) => v.RoboNeo_Web_system_setting,
                    )}
                  </div>
                  )}

                    {/* CLI 配置 */}
                    <div
                      className={styles.operateItem}
                      onClick={handleAccessKeyClick}
                    >
                      <IconFont name="keyBold" fontSize={16} />
                      {languageStore.smartcatTranslationText(
                        (v) => v.teams_CLI_configuration,
                      )}
                    </div>

                    {/* 条款与政策 */}
                    <Popover
                      placement="leftTop"
                      trigger={['hover']}
                      overlayStyle={{
                        zIndex: 'calc(var(--zindex-fixed) + 2) !important',
                      }}
                      overlayClassName="resetPopoverAndArrow"
                      content={(
                        <div
                          className={styles.popoverContent}
                          style={{
                            marginRight: '-15px',
                            minWidth: '200px',
                          }}
                        >
                          <div className={styles.operateList}>
                            <div
                              className={styles.operateItem}
                              onClick={() => {
                                window.mtstat?.track('terms_policy_click', {
                                  name: languageStore.smartcatTranslationText(
                                    (v) => v.RoboNeo_Web_user_agreement,
                                  ),
                                });
                                navigate({
                                  mode: 'url',
                                  openNewWindow: true,
                                  url: {
                                    pathname: '/user-agreement',
                                  },
                                  query: {
                                    countryCode: currentCountryCode,
                                    theme,
                                  },
                                });
                              }}
                            >
                              {languageStore.smartcatTranslationText(
                                (v) => v.RoboNeo_Web_user_agreement,
                              )}
                            </div>
                            <div
                              className={styles.operateItem}
                              onClick={() => {
                                window.mtstat?.track('terms_policy_click', {
                                  name: languageStore.smartcatTranslationText(
                                    (v) => v.RoboNeo_Web_privacy_policy,
                                  ),
                                });
                                navigate({
                                  mode: 'url',
                                  openNewWindow: true,
                                  url: {
                                    pathname: '/privacy-policy',
                                  },
                                  query: {
                                    countryCode: currentCountryCode,
                                    theme,
                                  },
                                });
                              }}
                            >
                              {languageStore.smartcatTranslationText(
                                (v) => v.RoboNeo_Web_privacy_policy,
                              )}
                            </div>
                            {!isOversea ? (
                              <>
                                <div
                                  className={styles.operateItem}
                                  onClick={() => {
                                    window.mtstat?.track('terms_policy_click', {
                                      name: languageStore.smartcatTranslationText(
                                        (v) => v.RoboNeo_Web_personal_collect_list,
                                      ),
                                    });
                                    navigate({
                                      mode: 'url',
                                      openNewWindow: true,
                                      url: {
                                        pathname: '/personal-info-collection-list',
                                      },
                                      query: {
                                        countryCode: currentCountryCode,
                                        theme,
                                      },
                                    });
                                  }}
                                >
                                  {languageStore.smartcatTranslationText(
                                    (v) => v.RoboNeo_Web_personal_collect_list,
                                  )}
                                </div>
                                <div
                                  className={styles.operateItem}
                                  onClick={() => {
                                    window.mtstat?.track('terms_policy_click', {
                                      name: languageStore.smartcatTranslationText(
                                        (v) => v.RoboNeo_Web_personal_info_share,
                                      ),
                                    });
                                    navigate({
                                      mode: 'url',
                                      openNewWindow: true,
                                      url: {
                                        pathname: '/third-party-sharing',
                                      },
                                      query: {
                                        countryCode: currentCountryCode,
                                        theme,
                                      },
                                    });
                                  }}
                                >
                                  {languageStore.smartcatTranslationText(
                                    (v) => v.RoboNeo_Web_personal_info_share,
                                  )}
                                </div>
                                <div
                                  className={styles.operateItem}
                                  onClick={() => {
                                    window.mtstat?.track('terms_policy_click', {
                                      name: '会员服务协议',
                                    });
                                    navigate({
                                      mode: 'url',
                                      openNewWindow: true,
                                      url: {
                                        pathname: '/membership-pay-agreement',
                                      },
                                      query: {
                                        countryCode: currentCountryCode,
                                        theme,
                                      },
                                    });
                                  }}
                                >
                                  {languageStore.smartcatTranslationText(
                                    (v) => v.RoboNeo_Web_member_service_agreement,
                                  )}
                                </div>
                              </>
                            ) : null}
                          </div>
                        </div>
                    )}
                    >
                      <div className={styles.operateItem}>
                        <IconFont name="infoCircleBold" fontSize={16} />
                        {languageStore.smartcatTranslationText(
                          (v) => v.RoboNeo_Web_rule_policy,
                        )}
                      </div>
                    </Popover>

                    {/* 意见反馈 */}
                    <div
                      className={styles.operateItem}
                      onClick={handleFeedbackClick}
                    >
                      <div className={styles.feedbackItem}>
                        <IconFont name="customerServiceBold" fontSize={16} />
                        {languageStore.smartcatTranslationText(
                          (v) => v.RoboNeo_Web_user_feedback,
                        )}
                        <span
                          className={classNames([
                            styles.unreadTip,
                            local.unreadCount ? styles.show : '',
                          ])}
                        />
                      </div>
                    </div>

                    {/* 订阅 */}
                    <div
                      className={styles.operateItem}
                      onClick={handleReportClick}
                    >
                      <IconFont
                        name="exclamationMarkTriangleBold"
                        fontSize={16}
                      />
                      {languageStore.smartcatTranslationText(
                        (v) => v.RoboNeo_Web_user_report,
                      )}
                    </div>
                  </div>
                  <div className={styles.line} />
                  <div
                    className={styles.logoutButton}
                    onClick={() => {
                      updateLocal({ loggedOutTipVisible: true });
                    }}
                  >
                    {languageStore.smartcatTranslationText(
                      (v) => v.RoboNeo_Web_logout_content,
                    )}
                  </div>
                </div>
            )}
            >

              <div className={styles.popoverButton}>
                <div
                  className={styles.avatarBox}
                  style={{
                    marginRight: rightMargin || 0,
                  }}
                >

                  <div className={styles.imageBox}>
                    {userInfo.avatar ? (
                      <Image
                        className={styles.image}
                        width={32}
                        height={32}
                        crossOrigin="anonymous"
                        src={userInfo.avatar}
                        alt="avatar"
                      />
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M8.00012 1C6.0302 1 4.50012 2.71737 4.50012 4.75C4.50012 6.78263 6.0302 8.5 8.00012 8.5C9.97005 8.5 11.5001 6.78263 11.5001 4.75C11.5001 2.71737 9.97005 1 8.00012 1Z"
                          fill="#F7F8FA"
                          fillOpacity="0.28"
                        />
                        <path
                          d="M8.00012 9.5C5.44726 9.5 3.22762 10.912 2.25842 12.97C2.03883 13.4363 2.127 13.921 2.39395 14.2735C2.65421 14.6171 3.07899 14.8333 3.54702 14.8333H12.4532C12.9213 14.8333 13.346 14.6171 13.6063 14.2735C13.8732 13.921 13.9614 13.4363 13.7418 12.97C12.7726 10.912 10.553 9.5 8.00012 9.5Z"
                          fill="#F7F8FA"
                          fillOpacity="0.28"
                        />
                      </svg>
                    )}
                    {/* vip 标识 */}
                    {showMembershipBadge && userInfo.subscribe?.isValid && (
                      <div className={styles['vip-tag']}>
                        <Image src={VipImg} width={16} height={6} alt="" crossOrigin="anonymous" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Popover>
            <Modal
              isModalOpen={local.showSettings}
              title={languageStore.smartcatTranslationText(
                (v) => v.RoboNeo_Web_toast_setting_text,
              )}
              close={() => {
                updateLocal({ showSettings: false });
              }}
            >
              {settings()}
            </Modal>
            <Modal
              modalClassName={styles.accessKeyModal}
              isModalOpen={local.showAccessKey}
              title={languageStore.smartcatTranslationText(
                (v) => v.my_access_keys,
              )}
              close={() => {
                updateLocal({ showAccessKey: false });
              }}
            >
              {accessKey()}
            </Modal>
            <Modal
              isModalOpen={local.loggedOutTipVisible}
              type="question"
              close={() => {
                updateLocal({ loggedOutTipVisible: false });
                window.mtstat?.track('logout_action_click', {
                  logout_result: 0,
                });
              }}
              cancel={() => {
                updateLocal({ loggedOutTipVisible: false });
                window.mtstat?.track('logout_action_click', {
                  logout_result: 0,
                });
              }}
              confirm={() => {
                updateLocal({ loggedOutTipVisible: false });
                toLogout();
                window.mtstat?.track('logout_action_click', {
                  logout_result: 1,
                });
              }}
            >
              {loggedOutTip()}
            </Modal>

            {/* AIGC 水印弹窗 */}
            <Modal
              type="warning"
              isModalOpen={openWatermarkModal}
              close={() => setOpenWatermarkModal(false)}
            >
              <div className={styles.watermark_tips_box}>
                <div className={styles.title}>
                  {/* 水印与使用须知 */}
                  {languageStore.smartcatTranslationText((v) => v.roboneo_web_watermark_usage_notice)}
                </div>
                <div className={styles.content}>
                  {languageStore.smartcatTranslationText((v) => v.roboneo_web_watermark_protocol)}
                </div>
                <div className={styles.button_box}>
                  <Button type="primary" onClick={continueCloseWatermarkHandler}>
                    {/* 同意并继续 */}
                    {languageStore.smartcatTranslationText((v) => v.roboneo_web_agree_continue)}
                  </Button>
                </div>

              </div>
            </Modal>
            <FeedbackModal
              userInfo={userInfo}
              env={getFebackEnv()}
              client_id={getEnv().CLIENT_ID}
              gid={getGid()}
              channel=""
              language={languageStore.languageCode}
              open={local.showFeedback}
              onClose={() => {
                updateLocal({ showFeedback: false });
              }}
              getAccessToken={getAccessToken}
              activeTab={local.unreadCount > 0 ? 'history' : ''}
            />

            <ReportModal
              userInfo={userInfo}
              env={getFebackEnv()}
              client_id={getEnv().CLIENT_ID}
              gid={getGid()}
              channel=""
              language={languageStore.languageCode}
              open={local.showReport}
              onClose={() => {
                updateLocal({ showReport: false });
              }}
              getAccessToken={getAccessToken}
            />
          </div>
        ) : (
          <Button
            className={styles.noLoginBox}
            buttonStyle={{
              marginRight: rightMargin,
            }}
            type="plain"
            onClick={() => {
              const searchParams = new URLSearchParams(window.location.search);
              window.mtstat?.track('roboneo_team_login_click', {
                room_id: searchParams.get('room_id') || '',
                project_id: searchParams.get('project_id') || '',
              });
              if (isOnline) {
                toLogin().then(() => {
                  initUserInfo();
                });
              } else {
                message.error('RoboNeo_Web_network_fail_retry');
              }
            }}
          >
            <IconFont name="userV3Bold" fontSize={16} />
            <div className={styles.noLoginButton}>
              {languageStore.smartcatTranslationText(
                (v) => v.RoboNeo_Web_login_content,
              )}
            </div>
          </Button>
        )}
      </div>

      {/* 创建团队弹窗 */}
      {openCreateTeamModal && (
        <CreateTeamModal
          teamList={teamList}
          onClose={() => setOpenCreateTeamModal(false)}
        />
      )}

      {openTeamManagementModal && (
        <TeamManagementModal
          open={openTeamManagementModal}
          onClose={handleTeamManagementClose}
        />
      )}
    </>
  );
}
